// Syntax colouring for Rails log messages on the black terminal panel of
// /admin/server_logs. tokenize() splits one message into [{ text, cls }]
// segments; withHighlight() then marks search matches on top of them, so a
// match keeps its colour and can span several segments.
//
// Colour scheme:
//   SQL statement        blue      (keywords brighter and bold)
//   durations (1.4ms)    green
//   query name           cyan      (User Load, CACHE Event Load, TRANSACTION)
//   bind values          gray
//   HTTP method          amber     path white
//   status               green 2xx, cyan 3xx, amber 4xx, red 5xx
//   controller#action    violet
//   parameters           orange
//   error class names    red
//   query source (↳ ...) and gem backtrace frames  dim gray, app frames orange

const C = {
  plain: 'text-gray-300',
  dim: 'text-gray-500',
  sql: 'text-blue-400',
  sqlKeyword: 'text-blue-300 font-semibold',
  time: 'text-green-400',
  name: 'text-cyan-300 font-semibold',
  cache: 'text-cyan-600',
  method: 'text-amber-300 font-bold',
  path: 'text-white font-semibold',
  controller: 'text-violet-300 font-semibold',
  params: 'text-orange-300',
  error: 'text-red-400 font-semibold',
  appFrame: 'text-orange-300',
  status2: 'text-green-400 font-bold',
  status3: 'text-cyan-300 font-bold',
  status4: 'text-amber-300 font-bold',
  status5: 'text-red-400 font-bold',
}

const SQL_START = /^(?:SELECT|INSERT|UPDATE|DELETE|BEGIN|COMMIT|ROLLBACK|SAVEPOINT|RELEASE|WITH|CREATE|DROP|ALTER|SET|SHOW|TRUNCATE|EXPLAIN|LOCK)\b/i
// "  User Load (1.4ms)  SELECT ...  [["id", 1]]", "  CACHE User Load (0.0ms) ...", "   (71.4ms)  CREATE ..."
const SQL_LINE = /^(\s*)([^()]*?)(\s?)(\()(\d+(?:\.\d+)?ms)(\))(\s+)([\s\S]+)$/
const SQL_BINDS = /^([\s\S]*?)(\s+\[\[[\s\S]*\]\])$/
const SQL_KEYWORDS = /\b(SELECT|DISTINCT|FROM|WHERE|AND|OR|NOT|IN|IS|NULL|INNER|LEFT|RIGHT|OUTER|JOIN|ON|AS|ORDER BY|GROUP BY|HAVING|LIMIT|OFFSET|INSERT INTO|VALUES|UPDATE|SET|DELETE|RETURNING|BEGIN|COMMIT|ROLLBACK|CASE|WHEN|THEN|ELSE|END|EXISTS|COUNT|ASC|DESC|UNION|WITH|CREATE|DROP|ALTER|TABLE|INDEX)\b/g

const STARTED = /^(Started )([A-Z]+)( ")([^"]*)(")([\s\S]*)$/
const COMPLETED = /^(Completed )(\d{3})([\s\S]*)$/
const PROCESSING = /^(Processing by )(\S+)( as )(\S+)([\s\S]*)$/
const PARAMETERS = /^(\s*Parameters: )([\s\S]*)$/
const SOURCE = /^(\s*↳ )([\s\S]*)$/
const BACKTRACE = /^(\s*)(\S+\.rb:\d+:in [\s\S]*)$/

// Anywhere in a line: durations and exception class names.
const GENERIC = /(\d+(?:\.\d+)?ms\b)|(\b(?:[A-Z]\w*::)*[A-Z]\w*(?:Error|Exception)\b)/g

function seg(text, cls = C.plain) {
  return text ? [{ text, cls }] : []
}

function generic(text, base = C.plain) {
  const out = []
  let last = 0
  for (const m of text.matchAll(GENERIC)) {
    out.push(...seg(text.slice(last, m.index), base))
    out.push({ text: m[0], cls: m[1] ? C.time : C.error })
    last = m.index + m[0].length
  }
  out.push(...seg(text.slice(last), base))
  return out
}

function sqlText(text) {
  const out = []
  let last = 0
  for (const m of text.matchAll(SQL_KEYWORDS)) {
    out.push(...seg(text.slice(last, m.index), C.sql))
    out.push({ text: m[0], cls: C.sqlKeyword })
    last = m.index + m[0].length
  }
  out.push(...seg(text.slice(last), C.sql))
  return out
}

function sqlLine(message) {
  const m = SQL_LINE.exec(message)
  if (!m || !SQL_START.test(m[8])) return null
  const [, indent, name, gap, open, time, close, spacing, rest] = m
  const binds = SQL_BINDS.exec(rest)
  return [
    ...seg(indent),
    ...seg(name, name.startsWith('CACHE') ? C.cache : C.name),
    ...seg(gap),
    ...seg(open, C.dim), ...seg(time, C.time), ...seg(close, C.dim),
    ...seg(spacing),
    ...(binds ? [...sqlText(binds[1]), ...seg(binds[2], C.dim)] : sqlText(rest)),
  ]
}

function statusClass(code) {
  return C[`status${code[0]}`] || C.plain
}

export function tokenize(message) {
  if (!message) return []

  const sql = sqlLine(message)
  if (sql) return sql

  let m
  if ((m = STARTED.exec(message))) {
    return [...seg(m[1]), ...seg(m[2], C.method), ...seg(m[3], C.dim), ...seg(m[4], C.path), ...seg(m[5], C.dim), ...generic(m[6], C.dim)]
  }
  if ((m = COMPLETED.exec(message))) {
    return [...seg(m[1]), ...seg(m[2], statusClass(m[2])), ...generic(m[3])]
  }
  if ((m = PROCESSING.exec(message))) {
    return [...seg(m[1]), ...seg(m[2], C.controller), ...seg(m[3]), ...seg(m[4], C.dim), ...generic(m[5])]
  }
  if ((m = PARAMETERS.exec(message))) {
    return [...seg(m[1], C.dim), ...seg(m[2], C.params)]
  }
  if ((m = SOURCE.exec(message))) {
    return [...seg(m[1], C.dim), ...seg(m[2], C.dim)]
  }
  if ((m = BACKTRACE.exec(message))) {
    return [...seg(m[1]), ...seg(m[2], /^(?:app|lib|config)\//.test(m[2]) ? C.appFrame : C.dim)]
  }
  return generic(message)
}

// Splits segments at the search matches (a RegExp with the g flag), marking
// the matching pieces with match: true.
export function withHighlight(segments, message, re) {
  if (!re || !message) return segments
  const ranges = []
  for (const m of message.matchAll(re)) {
    if (!m[0]) break
    ranges.push([m.index, m.index + m[0].length])
  }
  if (!ranges.length) return segments

  const out = []
  let offset = 0
  for (const { text, cls } of segments) {
    const start = offset
    const end = offset + text.length
    let cursor = start
    for (const [from, to] of ranges) {
      if (to <= cursor || from >= end) continue
      if (from > cursor) out.push({ text: message.slice(cursor, from), cls })
      const stop = Math.min(to, end)
      out.push({ text: message.slice(Math.max(from, cursor), stop), cls, match: true })
      cursor = stop
    }
    if (cursor < end) out.push({ text: message.slice(cursor, end), cls })
    offset = end
  }
  return out
}
